import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Container } from "@/components/common/Container";
import { AnimatedHeading } from "@/components/common/AnimatedHeading";
import { CtaButton } from "@/components/common/CtaButton";
import { Counter } from "@/components/common/Counter";
import { CursorGlow } from "@/components/common/CursorGlow";
import { siteConfig } from "@/config/site.config";
import { EASE } from "@/animations/motion";
import { HeroVisual } from "@/components/home/HeroVisual";
import { Sparkles, ArrowRight } from "@/icons";
import { heroStats, heroHeading, heroDescription } from "@/data/home.data";

/** Kinetic hero — gradient mesh, cursor glow, parallax product preview. */
export function Hero() {
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const mockupY = useTransform(scrollYProgress, [0, 1], [0, -90]);
  const mockupScale = useTransform(scrollYProgress, [0, 1], [1, 1.05]);
  const glowY = useTransform(scrollYProgress, [0, 1], [0, 140]);

  return (
    <section
      ref={ref}
      data-testid="hero"
      className="relative overflow-hidden pb-16 pt-32 sm:pt-40 lg:pb-24 lg:pt-48"
    >
      {/* Gradient mesh + grid background */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <motion.div
          style={{ y: glowY }}
          className="absolute -top-40 left-1/2 h-[620px] w-[980px] -translate-x-1/2 rounded-full bg-primary/[0.12] blur-[140px]"
        />
        <div className="absolute right-[6%] top-20 h-80 w-80 rounded-full bg-accent/[0.14] blur-[110px]" />
        <div className="absolute left-[4%] top-52 h-72 w-72 rounded-full bg-indigo-400/[0.10] blur-[110px]" />
        <div className="absolute inset-0 bg-grid opacity-60" />
      </div>
      <CursorGlow />

      <Container className="relative">
        {/* <motion.span
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: EASE }}
          className="inline-flex items-center gap-2 rounded-full border border-border bg-card/80 px-4 py-1.5 text-xs font-medium tracking-tight text-foreground shadow-sm backdrop-blur"
        >
          <Sparkles className="h-3.5 w-3.5 text-accent" />
          {siteConfig.business} • {siteConfig.tagline}
        </motion.span> */}

        <div className="grid items-center gap-14 lg:grid-cols-[1.35fr_0.9fr]">
          <div className="w-full max-w-3xl space-y-8">
            <motion.span
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: EASE }}
              className="inline-flex items-center gap-2 rounded-full border border-border bg-card/80 px-4 py-1.5 text-xs font-medium tracking-tight text-foreground shadow-sm backdrop-blur"
            >
              <Sparkles className="h-3.5 w-3.5 text-accent" />
              {siteConfig.business} • {siteConfig.tagline}
            </motion.span>

            <AnimatedHeading className="mt-7 max-w-4xl font-heading text-[2.6rem] font-extrabold leading-[1.08] tracking-tight text-foreground sm:text-5xl lg:text-[3rem]">
              Transforming Ideas Into{" "}
              <span className="inline text-primary">
                  AI-Powered
              </span>{" "}
              Business Solutions.
            </AnimatedHeading>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: EASE, delay: 0.55 }}
              className="mt-7 max-w-xl text-lg text-muted-foreground sm:text-xl"
            >
              {heroDescription}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: EASE, delay: 0.7 }}
              className="mt-9 flex flex-col gap-4 sm:flex-row sm:items-center"
            >
              <CtaButton
                to={siteConfig.primaryCta.path}
                size="lg"
                withArrow
                magnetic
                data-testid="hero-primary-cta"
              >
                {siteConfig.primaryCta.label}
              </CtaButton>
              <CtaButton
                to={siteConfig.secondaryCta.path}
                size="lg"
                variant="outline"
                data-testid="hero-secondary-cta"
              >
                {siteConfig.secondaryCta.label}
              </CtaButton>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, ease: EASE, delay: 0.9 }}
              className="mt-12 flex flex-wrap items-center gap-x-10 gap-y-6"
            >
              {heroStats.map((s) => (
                <div key={s.label} className="flex flex-col">
                  {/* <Counter
                    value={s.value}
                    className="font-heading text-3xl font-extrabold tracking-tight text-foreground"
                  /> */}
                  <span className="font-heading text-3xl font-extrabold tracking-tight text-foreground">
                    {s.value}
                  </span>
                  <span className="mt-1 text-sm text-muted-foreground">
                    {s.label}
                  </span>
                </div>
              ))}
            </motion.div>

          </div>
          <div className="relative flex justify-end">
            <div className="w-full max-w-[680px]">
                <HeroVisual />
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
}
